/**
 * EMAILS PAGE - FLO SIGNATURE DESIGN SYSTEM v2
 * Clean. Controlled. Consistent. Dark tool mode.
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ArrowPathIcon, PencilSquareIcon, MagnifyingGlassIcon, CogIcon, NoSymbolIcon } from '@heroicons/react/24/outline';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { SPACING } from '../design/tokens';
import { Screen, Section, Button, Tabs, Input } from '../design/primitives';
import { Alert } from '../components/ui';
import EmailList, { EmailListRef } from '../components/EmailList';
import ComposeEmail from '../components/ComposeEmail';
import EmailRuleManager from '../components/EmailRuleManager';
import BlockedSenderManager from '../components/BlockedSenderManager';
import { apiClient } from '../api/apiConfig';
import { EmailAccount } from '../types/email';

const EMAIL_TABS = [
  { id: 'inbox', label: 'Inbox' },
  { id: 'starred', label: 'Starred' },
  { id: 'needs_reply', label: 'Needs Reply' },
  { id: 'sent', label: 'Sent' },
  { id: 'newsletters', label: 'Newsletters' },
  { id: 'spam', label: 'Spam' },
  { id: 'all', label: 'All' },
];

const TAB_QUERY_MAP: Record<string, Record<string, string>> = {
  inbox: { is_archived: 'false', is_handled: 'false', is_newsletter: 'false' },
  starred: { is_starred: 'true' },
  needs_reply: { needs_reply: 'true' },
  sent: { is_outgoing: 'true' },
  newsletters: { is_newsletter: 'true' },
  spam: { is_spam: 'true' },
  all: {},
};

const EmailsPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [blockedOpen, setBlockedOpen] = useState(false);
  const [accounts, setAccounts] = useState<EmailAccount[]>([]);
  const [activeTab, setActiveTab] = useState('inbox');
  const [searchQuery, setSearchQuery] = useState('');
  const emailListRef = useRef<EmailListRef>(null);

  const staleGmailAccounts = useMemo(() => {
    const cutoff = Date.now() - 24 * 60 * 60 * 1000;
    return accounts.filter(a =>
      a.is_active && a.provider === 'gmail' &&
      (!a.last_sync_at || parseISO(a.last_sync_at).getTime() < cutoff)
    );
  }, [accounts]);

  useEffect(() => {
    apiClient.get('/api/email-accounts/').then(res => {
      setAccounts(res.data || []);
    }).catch(err => {
      console.error('Failed to fetch email accounts:', err);
    });
  }, []);

  // Handle Gmail OAuth callback redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gmailAuth = params.get('gmail_auth');
    if (!gmailAuth) return;

    // Clean URL params
    window.history.replaceState({}, '', window.location.pathname);

    if (gmailAuth === 'success') {
      setSuccess('Gmail re-authenticated successfully! Retrieving emails...');
      setTimeout(() => handleRetrieveEmails(), 500);
    } else if (gmailAuth === 'error') {
      const message = params.get('message') || 'Gmail authentication failed';
      setError(message);
    }
  }, []);

  const handleRetrieveEmails = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const response = await apiClient.post('/api/retrieve_emails/', {}, {
        timeout: 300000
      });

      if (response.data?.success) {
        const newEmails = response.data.new_emails || 0;
        setSuccess(`Retrieval Completed - ${newEmails} new email${newEmails !== 1 ? 's' : ''}!`);
      }

      emailListRef.current?.refresh();
    } catch (error: any) {
      const data = error.response?.data;
      if (data?.auth_required && data?.auth_url) {
        // Redirect to Google OAuth — callback will bring user back
        window.location.href = data.auth_url;
      } else {
        const errorMessage = data?.error || error.message || 'Failed to retrieve emails';
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Screen>
      <Section
        title="Emails"
        action={
          <div style={{ display: 'flex', gap: SPACING[8] }}>
            <Button
              variant="primary"
              onClick={() => setComposeOpen(true)}
              icon={<PencilSquareIcon style={{ width: '16px', height: '16px' }} />}
            >
              Compose
            </Button>
            <Button
              variant="secondary"
              onClick={handleRetrieveEmails}
              disabled={loading}
              icon={loading ? <ArrowPathIcon style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} /> : undefined}
            >
              {loading ? 'Retrieving...' : 'Retrieve Emails'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => setRulesOpen(true)}
              icon={<CogIcon style={{ width: '16px', height: '16px' }} />}
            >
              Rules
            </Button>
            <Button
              variant="secondary"
              onClick={() => setBlockedOpen(true)}
              icon={<NoSymbolIcon style={{ width: '16px', height: '16px' }} />}
            >
              Blocked
            </Button>
          </div>
        }
      >
        {/* Alerts */}
        {staleGmailAccounts.length > 0 && (
          <Alert variant="warning">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: SPACING[12], width: '100%' }}>
              <span>
                Gmail sync is stale for {staleGmailAccounts.map(a => a.email_address).join(', ')}
                {' '}— last sync {staleGmailAccounts[0].last_sync_at ? formatDistanceToNow(parseISO(staleGmailAccounts[0].last_sync_at)) + ' ago' : 'never'}.
              </span>
              <Button variant="secondary" onClick={handleRetrieveEmails} disabled={loading}>
                Reconnect
              </Button>
            </div>
          </Alert>
        )}
        {error && <Alert variant="error">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <Tabs tabs={EMAIL_TABS} activeTab={activeTab} onChange={setActiveTab} />

        <div style={{ marginBottom: SPACING[12] }}>
          <Input
            placeholder="Search emails..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            icon={<MagnifyingGlassIcon style={{ width: '16px', height: '16px' }} />}
          />
        </div>

        <EmailList
          ref={emailListRef}
          accounts={accounts}
          queryParams={TAB_QUERY_MAP[activeTab] || {}}
          searchQuery={searchQuery}
        />
      </Section>

      <ComposeEmail
        isOpen={composeOpen}
        onClose={() => setComposeOpen(false)}
        onSent={() => emailListRef.current?.refresh()}
        accounts={accounts}
      />

      <EmailRuleManager
        isOpen={rulesOpen}
        onClose={() => setRulesOpen(false)}
      />

      <BlockedSenderManager
        isOpen={blockedOpen}
        onClose={() => setBlockedOpen(false)}
      />
    </Screen>
  );
};

export default EmailsPage;
